import MenuIcon from "@mui/icons-material/Menu";

import {
  AppBar,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import useIsMobile from "components/hooks/useIsMobile";
import BrandAndUser from "components/ui/atoms/BrandAndUser";
import Highlight from "components/ui/atoms/Highlight";
import LeftNavigation from "components/ui/atoms/LeftNavigation";
import MenuFooter from "components/ui/atoms/MenuFooter";
import CustomLeftNavigation from "components/ui/customs/navigations/CustomLeftNavigation";
import { createContext, useContext, useEffect, useReducer } from "react";

const StyledDrawer = styled(Drawer)(
  ({ theme }) => `
  .MuiDrawer-paper {
    border-right: 1px solid ${theme.palette_custom.lightBlue};
  }
`,
);

export const LayoutContext = createContext();

const reducer = (context, action) => {
  switch (action.type) {
    case "toggle_drawer":
      return { ...context, leftDrawerOpen: !context.leftDrawerOpen };
    case "change_is_mobile":
      return { ...context, isMobile: action.isMobile };
    default:
      throw new Error(`unkown action : ${action.type}`);
  }
};

const LayoutContainer = ({
  children,
  drawerWidth,
  appBarHeight,
  isLoading,
  isError,
  hideBreadcrumbs,
  config,
  app,
}) => {
  const [context, dispatch] = useReducer(reducer, {
    appBarHeight: appBarHeight || 64,
    drawerWidth: drawerWidth || 240,
    isMobile: false,
    leftDrawerOpen: false,
    hideBreadcrumbs: hideBreadcrumbs || false,
    app: app || "ringapp",
  });
  const isMobile = useIsMobile();

  useEffect(() => {
    dispatch({ type: "change_is_mobile", isMobile: isMobile });
  }, [isMobile]);

  return (
    <LayoutContext.Provider value={{ context, dispatch }}>
      <LayoutLeftDrawer>
        <LayoutBrand>
          <BrandAndUser />
        </LayoutBrand>
        {!config && <LeftNavigation />}
        {config && <CustomLeftNavigation config={config} />}
        <LayoutFooter>
          <MenuFooter app={app || "ringapp"} />
        </LayoutFooter>
      </LayoutLeftDrawer>
      <Box
        sx={{
          pt: `${context.appBarHeight}px`,
          pl: `${context.isMobile ? 0 : context.drawerWidth}px`,
        }}
      >
        {isError && <Typography>Error</Typography>}
        {isLoading && !isError && <CircularProgress />}
        {!isLoading && !isError && children}
      </Box>
    </LayoutContext.Provider>
  );
};

const LayoutLeftDrawer = ({ children }) => {
  const { context, dispatch } = useContext(LayoutContext);

  return (
    <>
      <StyledDrawer
        variant={context.isMobile ? "temporary" : "permanent"}
        open={context.leftDrawerOpen}
        ModalProps={{
          onClose: () => dispatch({ type: "toggle_drawer" }),
        }}
        PaperProps={{
          sx: {
            width: context.drawerWidth,
            pt: `${context.appBarHeight}px`,
          },
        }}
      >
        {children}
      </StyledDrawer>
    </>
  );
};

const LayoutBrand = ({ children }) => {
  const { context } = useContext(LayoutContext);

  return (
    <>
      <Box
        sx={{
          top: 0,
          position: "fixed",
          zIndex: 1201,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: context.drawerWidth,
            height: context.appBarHeight,
            boxSizing: "border-box",
            backgroundColor: "white",
            borderBottom: 1,
          }}
        >
          {children}
        </Box>
      </Box>
    </>
  );
};

const LayoutFooter = ({ children }) => {
  const { context } = useContext(LayoutContext);

  return (
    <>
      <Box
        sx={{
          bottom: 0,
          position: "fixed",
          width: context.drawerWidth,
          display: "flex",
          alignItems: "center",
          flexWrap: "nowrap",
          justifyContent: "center",
          backgroundColor: "white",
        }}
      >
        {children}
      </Box>
    </>
  );
};

const LayoutTitle = ({ children, variant, color }) => {
  const { context, dispatch } = useContext(LayoutContext);

  let variant_children;
  if (variant == "component") {
    variant_children = children;
  } else {
    variant_children = <Highlight color={color}>{children}</Highlight>;
  }

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          height: context.appBarHeight,
          pl: `${context.isMobile ? 0 : context.drawerWidth}px`,
          borderBottom: 1,
          borderColor: "black",
          backgroundColor: "white",
          boxShadow: "none",
          color: "black",
        }}
      >
        <Toolbar>
          {context.isMobile && (
            <IconButton
              edge="start"
              onClick={() => dispatch({ type: "toggle_drawer" })}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Stack
            direction="column"
            alignItems="start"
            width={1}
            height={1}
            justifyContent={"center"}
          >
            {variant_children}
          </Stack>
        </Toolbar>
      </AppBar>
    </>
  );
};

const LayoutContent = ({ children, ...props }) => {
  const isMobile = useIsMobile();
  return (
    <>
      <Box
        {...props}
        sx={{
          p: 2,
          overflowX: "scroll",
          ...props.sx,
        }}
      >
        {children}
      </Box>
    </>
  );
};

const Layout = ({ children, container, title, content, ...props }) => {
  if (container)
    return <LayoutContainer {...props}>{children}</LayoutContainer>;
  if (title) return <LayoutTitle {...props}>{children}</LayoutTitle>;
  if (content) return <LayoutContent {...props}>{children}</LayoutContent>;
};

export default Layout;
